import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlowByPhaseDataOverTime } from '../api/work-periods-client/work-periods-client.type';
import { resolvePhaseLabels } from '../helpers/phase-labels/phase-labels';
import type { PhaseLabel, ResolvedPhaseLabels } from '../helpers/phase-labels/phase-labels.type';
import { extractPhaseKeys } from '../helpers/subgroup-helpers/subgroup-helpers';

type MappedPhasesContextType = {
  mappedPhases: string[];
  resolvedLabels: ResolvedPhaseLabels;
  isLoading: boolean;
};

const MappedPhasesContext = createContext<MappedPhasesContextType | null>(null);

type MappedPhasesProviderProps = {
  children: ReactNode;
  flowData: FlowByPhaseDataOverTime | undefined;
  phases?: PhaseLabel[];
  isLoading?: boolean;
};

/**
 * Provides chart consumers with the dynamically discovered set of phase
 * keys present in the response and the canonical-keyed display label map
 * resolved from the optional `phases` companion array.
 *
 * Responsibilities:
 * 1. Compute `mappedPhases` from `flowData` so legend/tile components only
 *    render phases that actually have data this period.
 * 2. Compute `resolvedLabels` once per render via `resolvePhaseLabels`,
 *    preferring backend overrides over the `common.workflow_steps.*` i18n
 *    defaults — kept inside the provider so every consumer reads the same
 *    map without re-running the merge.
 * 3. Memoize the context value object so consumer re-renders are gated on
 *    the underlying inputs, not the provider's parent rendering.
 *
 * @param {ReactNode} children - The React subtree that consumes the context.
 * @param {FlowByPhaseDataOverTime | undefined} flowData - Phase-by-time
 *   measure data from the API; drives `mappedPhases`.
 * @param {PhaseLabel[]} [phases] - Optional `phases` companion array from
 *   the same API response. Absent for non-phase-aware responses; the
 *   resolver falls back to i18n defaults in that case.
 * @param {boolean} [isLoading] - External loading flag merged with the
 *   "no flow data yet" condition so consumers have a single signal.
 */
export function MappedPhasesProvider({ children, flowData, phases, isLoading = false }: MappedPhasesProviderProps) {
  const { t } = useTranslation();

  const mappedPhases = useMemo(() => {
    if (!flowData) {
      return [];
    }
    return extractPhaseKeys(flowData);
  }, [flowData]);

  const resolvedLabels = useMemo(() => resolvePhaseLabels(phases, t), [phases, t]);

  const value = useMemo(
    () => ({
      mappedPhases,
      resolvedLabels,
      isLoading: isLoading || !flowData,
    }),
    [mappedPhases, resolvedLabels, isLoading, flowData],
  );

  return <MappedPhasesContext.Provider value={value}>{children}</MappedPhasesContext.Provider>;
}

/**
 * Reads the mapped-phases context. Falls back to an empty `mappedPhases`
 * list and resolver-default labels when called outside a provider so a
 * forgetful consumer renders i18n defaults rather than crashing.
 *
 * @returns {MappedPhasesContextType} The current mapped-phases context.
 */
export function useMappedPhases(): MappedPhasesContextType {
  const ctx = useContext(MappedPhasesContext);
  const { t } = useTranslation();
  // Compute the fallback unconditionally so the hook call order is stable
  // when a consumer is re-mounted between provider/no-provider trees.
  const defaultLabels = useMemo(() => resolvePhaseLabels(undefined, t), [t]);
  // Memoize the synthetic fallback object so consumer `useMemo`s keyed on
  // `mappedPhases` don't invalidate every render when no provider wraps
  // the consumer.
  const fallback = useMemo<MappedPhasesContextType>(
    () => ({
      mappedPhases: [],
      resolvedLabels: defaultLabels,
      isLoading: false,
    }),
    [defaultLabels],
  );
  return ctx ?? fallback;
}
